'use client';

import { GraduationCap, Briefcase, Shield, Home, Users, Star } from 'lucide-react';
import { LifeEvent } from '@/lib/types';
import { cn } from '@/lib/utils';

interface LifeTimelineProps {
  events: LifeEvent[];
}

const eventIcons: Record<LifeEvent['eventType'], React.ReactNode> = {
  education: <GraduationCap className="w-5 h-5" />,
  work: <Briefcase className="w-5 h-5" />,
  military: <Shield className="w-5 h-5" />,
  residence: <Home className="w-5 h-5" />,
  membership: <Users className="w-5 h-5" />,
  milestone: <Star className="w-5 h-5" />,
};

const eventColors: Record<LifeEvent['eventType'], string> = {
  education: 'bg-blue-100 text-blue-700 border-blue-200',
  work: 'bg-amber-100 text-amber-700 border-amber-200',
  military: 'bg-green-100 text-green-700 border-green-200',
  residence: 'bg-purple-100 text-purple-700 border-purple-200',
  membership: 'bg-rose-100 text-rose-700 border-rose-200',
  milestone: 'bg-memorial-accent/20 text-memorial-secondary border-memorial-accent/30',
};

export function LifeTimeline({ events }: LifeTimelineProps) {
  if (!events || events.length === 0) return null;

  const sortedEvents = [...events].sort((a, b) => a.sortOrder - b.sortOrder);

  const formatYears = (yearStart: number | null, yearEnd: number | null): string => {
    if (!yearStart && !yearEnd) return '';
    if (yearStart && !yearEnd) return `${yearStart} – heute`;
    if (!yearStart && yearEnd) return `– ${yearEnd}`;
    if (yearStart === yearEnd) return `${yearStart}`;
    return `${yearStart} – ${yearEnd}`;
  };

  return (
    <section className="py-12 bg-memorial-muted/30">
      <div className="max-w-3xl mx-auto px-4">
        <h2 className="font-serif text-2xl md:text-3xl text-memorial-primary text-center mb-10">
          Lebenslauf
        </h2>

        <div className="relative">
          {/* Timeline line */}
          <div className="absolute left-6 md:left-1/2 top-0 bottom-0 w-0.5 bg-memorial-muted md:-translate-x-1/2" />

          <div className="space-y-8">
            {sortedEvents.map((event, index) => (
              <div
                key={event.id}
                className={cn(
                  'relative flex items-start gap-4 md:gap-8',
                  index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                )}
              >
                {/* Timeline dot */}
                <div className="absolute left-6 md:left-1/2 w-3 h-3 rounded-full bg-memorial-secondary border-4 border-memorial-background md:-translate-x-1/2 z-10" />

                {/* Content */}
                <div className={cn(
                  'ml-14 md:ml-0 md:w-[calc(50%-2rem)] bg-white rounded-xl p-5 shadow-sm border border-memorial-muted animate-fade-in',
                  index % 2 === 0 ? 'md:mr-auto md:text-right' : 'md:ml-auto md:text-left'
                )}>
                  {/* Years */}
                  <div className="text-sm font-medium text-memorial-secondary mb-2">
                    {formatYears(event.yearStart, event.yearEnd)}
                  </div>

                  {/* Icon and title */}
                  <div className={cn(
                    'flex items-center gap-3 mb-2',
                    index % 2 === 0 ? 'md:flex-row-reverse' : ''
                  )}>
                    <span className={cn(
                      'flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center border',
                      eventColors[event.eventType]
                    )}>
                      {eventIcons[event.eventType]}
                    </span>
                    <h3 className="font-medium text-memorial-text-primary">
                      {event.title}
                    </h3>
                  </div>

                  {/* Location */}
                  {event.location && (
                    <p className="text-sm text-memorial-text-secondary">
                      {event.location}
                    </p>
                  )}

                  {/* Description */}
                  {event.description && (
                    <p className="text-sm text-memorial-text-secondary mt-1 italic">
                      {event.description}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
